import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, Loader2, Undo2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { errorMessage } from '../../../lib/errors';

interface VoidReissueModalProps {
    jobId: string;
    jobLabel: string;
    onClose: () => void;
    // Called with the new (reissued) job id after a successful void.
    onVoided: (newJobId: string) => void;
}

/**
 * Void & Reissue — the only way to change a paid (locked) job.
 * A reason is REQUIRED; it is persisted on the job row and in audit_logs by
 * fn_void_and_reissue, which also posts reversing ledger entries, voids the
 * original job + invoice, and creates an editable copy.
 */
export const VoidReissueModal = ({ jobId, jobLabel, onClose, onVoided }: VoidReissueModalProps) => {
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canSubmit = reason.trim().length > 0 && !busy;

    const handleConfirm = async () => {
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        try {
            const { data, error: rpcError } = await supabase.rpc('fn_void_and_reissue', {
                p_job_id: jobId,
                p_reason: reason.trim(),
            });
            if (rpcError) throw rpcError;
            onVoided(data as string);
        } catch (err) {
            console.error('Void & reissue failed:', err);
            setError(errorMessage(err) || 'Could not void this job.');
            setBusy(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-scrim/70 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
            <div className="relative w-full max-w-md bg-panel border border-line rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-start justify-between p-5 border-b border-line">
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-gold-fill/10 border border-gold/20">
                            <AlertTriangle className="w-5 h-5 text-gold" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-fg">Void &amp; Reissue {jobLabel}</h2>
                            <p className="text-xs text-fg-3 mt-0.5">
                                This job's invoice is paid. Voiding reverses the booked income, marks the original
                                void, and creates an editable copy. Nothing is deleted.
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} disabled={busy} className="p-1 text-fg-4 hover:text-fg disabled:opacity-40">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-3">
                    <label className="block">
                        <span className="text-xs font-semibold text-fg-3 uppercase tracking-wider">
                            Reason (required — kept for audit)
                        </span>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            autoFocus
                            placeholder="e.g. Wrong part billed; customer disputed the engine charge…"
                            className="mt-1.5 w-full bg-card border border-line-strong focus:border-accent text-fg text-sm rounded-lg px-3 py-2.5 outline-none transition-colors resize-none"
                        />
                    </label>
                    {error && (
                        <p className="text-xs text-danger bg-danger/10 border border-danger/20 rounded-lg px-3 py-2">
                            {error}
                        </p>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-line bg-raised">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="px-4 py-2 rounded-lg bg-raised hover:bg-pressed text-fg text-sm font-medium border border-line-strong disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!canSubmit}
                        title={reason.trim() ? undefined : 'Enter a reason first'}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gold-fill hover:bg-gold-fill text-on-gold text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                        Void &amp; Reissue
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};
